// MetaTags Component - Dynamic Meta Tag Management
// Created: 2025-12-07
// Purpose: Dynamically update HTML meta tags for SEO optimization
// Updates: title, description, keywords, canonical, robots, author, viewport

import { useEffect } from 'react';
import { SEOPageData } from './types';

interface MetaTagsProps {
  seoData: SEOPageData;
}

const MetaTags: React.FC<MetaTagsProps> = ({ seoData }) => {
  useEffect(() => {
    // Update document title
    if (seoData.meta?.title) {
      document.title = seoData.meta.title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (
      name: string,
      content: string,
      attribute: 'name' | 'property' = 'name'
    ) => {
      let metaTag = document.querySelector(
        `meta[${attribute}="${name}"]`
      ) as HTMLMetaElement;

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(attribute, name);
        document.head.appendChild(metaTag);
      }

      metaTag.content = content;
    };

    // Update basic meta tags
    if (seoData.meta) {
      const { meta } = seoData;

      if (meta.description) updateMetaTag('description', meta.description);
      if (meta.keywords?.length)
        updateMetaTag('keywords', meta.keywords.join(', '));
      if (meta.author) updateMetaTag('author', meta.author);
      if (meta.robots) updateMetaTag('robots', meta.robots);
      if (meta.viewport) updateMetaTag('viewport', meta.viewport);
      if (meta.language) updateMetaTag('language', meta.language);
      if (meta.revisitAfter) updateMetaTag('revisit-after', meta.revisitAfter);
      if (meta.googleSiteVerification)
        updateMetaTag('google-site-verification', meta.googleSiteVerification);
    }

    // Update Open Graph tags
    if (seoData.openGraph) {
      const { openGraph } = seoData;

      if (openGraph.title)
        updateMetaTag('og:title', openGraph.title, 'property');
      if (openGraph.description)
        updateMetaTag('og:description', openGraph.description, 'property');
      if (openGraph.image)
        updateMetaTag('og:image', openGraph.image, 'property');
      if (openGraph.imageAlt)
        updateMetaTag('og:image:alt', openGraph.imageAlt, 'property');
      if (openGraph.url) updateMetaTag('og:url', openGraph.url, 'property');
      if (openGraph.type) updateMetaTag('og:type', openGraph.type, 'property');
      if (openGraph.siteName)
        updateMetaTag('og:site_name', openGraph.siteName, 'property');
      if (openGraph.locale)
        updateMetaTag('og:locale', openGraph.locale, 'property');

      // Additional OG image properties
      if (openGraph.imageWidth)
        updateMetaTag('og:image:width', openGraph.imageWidth, 'property');
      if (openGraph.imageHeight)
        updateMetaTag('og:image:height', openGraph.imageHeight, 'property');
      if (openGraph.imageType)
        updateMetaTag('og:image:type', openGraph.imageType, 'property');
      if (openGraph.imageSecureUrl)
        updateMetaTag('og:image:secure_url', openGraph.imageSecureUrl, 'property');

      // Article-specific OG tags
      if (openGraph.type === 'article' && openGraph.article) {
        const { article } = openGraph;
        if (article.publishedTime)
          updateMetaTag('article:published_time', article.publishedTime, 'property');
        if (article.modifiedTime)
          updateMetaTag('article:modified_time', article.modifiedTime, 'property');
        if (article.author)
          updateMetaTag('article:author', article.author, 'property');
        if (article.section)
          updateMetaTag('article:section', article.section, 'property');
        if (article.tags?.length) {
          article.tags.forEach((tag) => {
            updateMetaTag('article:tag', tag, 'property');
          });
        }
      }
    }

    // Update Twitter Card tags
    if (seoData.twitter) {
      const { twitter } = seoData;

      if (twitter.card) updateMetaTag('twitter:card', twitter.card);
      if (twitter.site) updateMetaTag('twitter:site', twitter.site);
      if (twitter.creator) updateMetaTag('twitter:creator', twitter.creator);
      if (twitter.title) updateMetaTag('twitter:title', twitter.title);
      if (twitter.description)
        updateMetaTag('twitter:description', twitter.description);
      if (twitter.image) updateMetaTag('twitter:image', twitter.image);
      if (twitter.imageAlt) updateMetaTag('twitter:image:alt', twitter.imageAlt);
    }

    // Update canonical link
    const canonicalUrl = seoData.meta?.canonical || window.location.origin + window.location.pathname;
    if (canonicalUrl) {
      let canonicalLink = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement;

      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }

      canonicalLink.href = canonicalUrl;
    }

    // Add hreflang for language alternatives (if needed in future)
    // This helps with international SEO
  }, [seoData]);

  // Preload this route's above-the-fold imagery.
  //
  // Kept in its own effect with a cleanup pass: the tags are only useful while
  // their route is on screen, and leaving them behind would have the browser
  // holding connections open for images the next page never asks for. They are
  // marked with data-seo-preload so we only ever remove our own.
  const preloadImages = seoData.preloadImages;
  useEffect(() => {
    if (!preloadImages?.length) return;

    const links = preloadImages.map(href => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      link.setAttribute('data-seo-preload', 'true');
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      return link;
    });

    return () => links.forEach(link => link.remove());
  }, [preloadImages]);

  return null; // This component only manages head tags
};

export default MetaTags;
